'use client';

import { sessionCookieFetch } from '@sourcepilot/ui/framework/data/dag-sync/organisation-change';
import { sessionChangeFailed, sessionChangeRefused } from '@sourcepilot/ui/framework/data/session-change-refusal';

/**
 * Hook for managing admin mode and admin write mode
 *
 * Admin mode allows admin users to bypass workspace security filters:
 * - admin_mode: Can READ all workspaces (but writes to other workspaces blocked)
 * - admin_write_mode: Can also WRITE to all workspaces (requires admin_mode)
 *
 * - Requires the user to be an admin (is_admin from /auth/user)
 * - NOT persisted to localStorage (security measure - resets on page refresh)
 * - Syncs with backend via POST /auth/admin-mode and /auth/admin-write-mode
 */

import { useEffect, useCallback, useState } from 'react';
import { useAtom } from 'jotai';
import { adminModeAtom, adminWriteModeAtom } from '@sourcepilot/ui/shell/state/ui-atoms';
import { useSimpleAuth } from '@sourcepilot/ui/shell/auth/use-simple-auth';

const SOURCEPILOT_SERVER_URL = process.env.NEXT_PUBLIC_SOURCEPILOT_URL;

export function useAdminMode() {
  const [adminMode, setAdminModeAtom] = useAtom(adminModeAtom);
  const [adminWriteMode, setAdminWriteModeAtom] = useAtom(adminWriteModeAtom);
  const { user, refetch } = useSimpleAuth();

  // Defer isAdmin to after mount to prevent hydration mismatch
  // (server has user=null, client may have cached user with is_admin=true)
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => { setHasMounted(true); }, []);

  const isAdmin = hasMounted && user?.is_admin === true;

  // Sync local state with backend state from user data
  useEffect(() => {
    setAdminModeAtom(user?.admin_mode === true);
    setAdminWriteModeAtom(user?.admin_write_mode === true);
  }, [user?.admin_mode, user?.admin_write_mode, setAdminModeAtom, setAdminWriteModeAtom]);

  // Toggle admin mode via dedicated endpoint
  const setAdminMode = useCallback(async (enabled: boolean) => {
    if (!isAdmin) return;

    try {
      const response = await sessionCookieFetch(
        `${SOURCEPILOT_SERVER_URL}/auth/admin-mode?enabled=${enabled}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      if (response.ok) {
        setAdminModeAtom(enabled);
        // Disabling admin mode also disables write mode
        if (!enabled) {
          setAdminWriteModeAtom(false);
        }
        refetch(); // Refresh user data to stay in sync
      } else {
        await sessionChangeRefused(response, 'Admin mode was not changed.');
      }
    } catch (error) {
      console.error('Failed to toggle admin mode:', error);
      sessionChangeFailed(error, 'Admin mode was not changed.');
    }
  }, [isAdmin, setAdminModeAtom, setAdminWriteModeAtom, refetch]);

  // Toggle admin write mode via dedicated endpoint
  const setAdminWriteMode = useCallback(async (enabled: boolean) => {
    if (!isAdmin || !adminMode) return;

    try {
      const response = await sessionCookieFetch(
        `${SOURCEPILOT_SERVER_URL}/auth/admin-write-mode?enabled=${enabled}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      if (response.ok) {
        setAdminWriteModeAtom(enabled);
        refetch(); // Refresh user data to stay in sync
      } else {
        await sessionChangeRefused(response, 'Admin write mode was not changed.');
      }
    } catch (error) {
      console.error('Failed to toggle admin write mode:', error);
      sessionChangeFailed(error, 'Admin write mode was not changed.');
    }
  }, [isAdmin, adminMode, setAdminWriteModeAtom, refetch]);

  return {
    adminMode: isAdmin && adminMode,
    adminWriteMode: isAdmin && adminMode && adminWriteMode,
    isAdmin,
    setAdminMode,
    setAdminWriteMode,
  };
}
